import { keyframes, styled } from 'styled-components';

import { MainContainer } from 'ui/styles/containers';

const logoAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(9em);
  }
  60% {
    opacity: 1;
    transform: translateY(9em)
  }
  100% {
    opacity: 1;
    transform: translateY(0)
  }
`;

const logoAnimationWithoutTranslate = keyframes`
  0% {
    opacity: 0;
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
  }
  100% {
    opacity: 1;
    visibility: visible;
  }
`;

const animationTiming = {
  logo: 1.25,
  heading: 1,
  subheading: 2,
};

const LogoWrapper = styled.div`
  animation: ${logoAnimation} ${animationTiming.logo}s forwards ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    animation: ${logoAnimationWithoutTranslate} ${animationTiming.logo}s forwards ease-in-out;
  }
`;

const ContainerTop = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto 0;
  padding: 30px 0;
  max-width: 30em;

  & > * {
    margin: 15px 0;
    text-align: center;
    text-wrap: balance;
  }

  & strong {
    font-weight: 400;
    color: ${theme.colors.helsinkiBlue};
    white-space: nowrap;

    /* Force a line break before the strong element */
    @media (width > 25em) {
      &::before {
        content: '';
        display: block;
      }
    }
  }

  & svg {
    width: 100px;
    height: 100px;
  }
`,
);

// Animations
// 1. Everything is hidden
// 2. Logo appears in the middle of the screen
// 3. First paragraph appears below the logo
// 4. Second paragraph appears below the first paragraph
// 5. Button appears below the second paragraph
const StyledMainContainer = styled(MainContainer)`
  justify-content: center;
  flex: auto;
  color: red;

  & [data-animate] {
    opacity: 0;
    visibility: hidden;
    animation-name: ${fadeIn};
    animation-duration: 1.25s;
    animation-fill-mode: forwards;
    animation-timing-function: ease-in-out;
  }

  & [data-animate='heading'] {
    animation-delay: ${animationTiming.logo}s;
  }

  & [data-animate='subheading'] {
    animation-delay: ${animationTiming.logo + animationTiming.heading}s;
  }

  & [data-animate='button'] {
    animation-delay: ${animationTiming.logo + animationTiming.heading + animationTiming.subheading}s;
  }
`;

const ButtonContainer = styled.div`
  margin: 10px 0;
  max-width: 365px;
  width: 100%;
`;

export interface AnimatedSpotlightProps {
  logo?: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  buttons: React.ReactNode;
  containerProps?: React.ComponentProps<typeof StyledMainContainer>;
}

export const AnimatedSpotlight = ({ logo, title, subtitle, buttons, containerProps }: AnimatedSpotlightProps) => (
  <StyledMainContainer {...containerProps}>
    <ContainerTop>
      {logo && <LogoWrapper>{logo}</LogoWrapper>}
      {title}
      {subtitle}
    </ContainerTop>
    <ButtonContainer>{buttons}</ButtonContainer>
  </StyledMainContainer>
);
